import type { TFunction } from 'i18next';
import { isEmpty } from 'lodash';
import concatText from 'classnames';
// Import Types
import type { EfficiencyReportItem } from 'types';
import type { ChartData, ChartFormatterParams, SeriesDataPoint } from './types';
// Import Helpers
import { customMomentWithoutTimezoneConversion, formatNumber } from 'services/Helpers';
// Import Enums
import { FormatNumberActionEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
import {
    PERCENTAGE_DOWN_COLOR,
    INITIAL_CHART_DATA,
    NEXT_VALUE_COLOR,
    PREVIOUS_VALUE_COLOR,
    PERCENTAGE_UP_COLOR,
} from './constants';

export const parseEfficiencyReportToChartData = (data: EfficiencyReportItem[], t: TFunction): ChartData => {
    if (isEmpty(data)) {
        return INITIAL_CHART_DATA;
    }

    const categories: string[] = [];
    const previousData: SeriesDataPoint[] = [];
    const nextData: SeriesDataPoint[] = [];

    const previousSeriesName = t(l.BeforePromo);
    const nextSeriesName = t(l.AfterPromo);

    const currency = data[0].CurrencyId ?? '';

    data.forEach(({ PreviousValue, NextValue, Count, Date }) => {
        categories.push(customMomentWithoutTimezoneConversion(Date).format('MMM DD'));

        const percentageCalculation = ((NextValue - PreviousValue) / PreviousValue) * 100;

        previousData.push({
            y: PreviousValue,
            name: previousSeriesName,
            clientCount: Count,
        });
        nextData.push({
            y: NextValue,
            name: nextSeriesName,
            clientCount: Count,
            percentageCalculation,
            isNext: true,
        });
    });

    return {
        categories,
        series: [
            {
                name: previousSeriesName,
                type: 'column',
                color: PREVIOUS_VALUE_COLOR,
                data: previousData,
            },
            {
                name: nextSeriesName,
                type: 'column',
                color: NEXT_VALUE_COLOR,
                data: nextData,
            },

            {
                name: previousSeriesName,
                type: 'spline',
                color: PREVIOUS_VALUE_COLOR,
                visible: false,
                data: previousData,
            },
            {
                name: nextSeriesName,
                type: 'spline',
                color: NEXT_VALUE_COLOR,
                visible: false,
                data: nextData,
            },
        ],
        currency,
    };
};

const getPercentageColor = (percentage: number) => {
    if (percentage === 0) return 'gray';
    return percentage > 0 ? PERCENTAGE_UP_COLOR : PERCENTAGE_DOWN_COLOR;
};

export const chartFormatter = ({ bar, metricName, chartData, t }: ChartFormatterParams) => {
    const { y, point } = bar;
    const { isNext, percentageCalculation, clientCount } = point;

    const formattedY = formatNumber(y, {
        type: FormatNumberActionEnum.DecimalPrecision,
        precision: 2,
    });

    const formattedPercentageCalculation =
        formatNumber(percentageCalculation ?? '', {
            type: FormatNumberActionEnum.DecimalPrecision,
            precision: 2,
        }) ?? 0;

    const percentageHTML = `<span style="color: ${getPercentageColor(+formattedPercentageCalculation)}">(${formattedPercentageCalculation}%)</span>`;

    const priceWithPercentageHTML = concatText(formattedY, chartData?.currency, { [percentageHTML]: isNext });

    const tooltipText = `<span>${metricName}: <b>${priceWithPercentageHTML}</b></span><br/><span>${t(l.ClientsCount)}: <b>${clientCount}</b></span>`;

    return tooltipText;
};
